import { differenceInMinutes, format, isToday } from "date-fns";

import { convertCamelCaseToCapitalize, convertSnakeCaseToNoCaseCapitalize } from "@/utils/str";

/** Machine names whose capitalized form is not the product wording. */
const STATUS_DISPLAY_OVERRIDES: Record<string, string> = {
  sleeperBed: "Sleeper Berth",
};

/** Dispatcher-facing label for a duty or driving status machine name; empty when unknown. */
export function formatStatusLabel(raw: string | null | undefined): string {
  if (!raw) return "";
  return STATUS_DISPLAY_OVERRIDES[raw] ?? convertCamelCaseToCapitalize(convertSnakeCaseToNoCaseCapitalize(raw));
}

/** Dispatcher-facing label for a `calculatedAvailability` value; empty when unknown. */
export function formatAvailabilityLabel(raw: string | null | undefined): string {
  switch (raw) {
    case "present":
      return "Available";
    case "notPresent":
      return "Unavailable";
    case "unknown":
      return "Unknown";
    default:
      return "";
  }
}

/** The device volume as the percent the driver sees; empty when unknown. */
export function formatVolume(volume: number | null | undefined): string {
  return typeof volume === "number" ? `${Math.round(volume * 100)}%` : "";
}

/** "Yes"/"No" for a projected boolean field. */
export function formatYesNo(value: string | boolean | null | undefined): string {
  return value === true || value === "true" ? "Yes" : "No";
}

/**
 * A message time as the table shows it: the clock time when it is today, otherwise the date and clock time. Absolute
 * rather than relative so a row never goes stale between data refreshes.
 */
export function formatItemTime(iso: string | null | undefined): { short: string; full: string } | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return {
    short: isToday(date) ? format(date, "HH:mm") : format(date, "MMM d, HH:mm"),
    full: format(date, "EEE, MMM d yyyy 'at' HH:mm:ss"),
  };
}

/**
 * Words for how far an instant sits from now: "42 min", "3 hr", "2 days", or null once a week separates them.
 */
function ageWords(minutes: number): string | null {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)} hr`;
  if (minutes < 60 * 24 * 7) {
    const days = Math.floor(minutes / (60 * 24));
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  return null;
}

/**
 * A timestamp as the table's Time column shows it: the local clock time, the age in words, and the full form for a
 * tooltip. The clock is `HH:mm:ss` today and the date with `HH:mm` before that; the age reads "just now", "42 min ago",
 * "3 hr ago", "2 days ago", then nothing once the clock already names the day. An instant still ahead, such as a
 * planned time of availability, reads "in 42 min" on the same scale. `now` is passed in so the table re-renders every
 * minute from one clock.
 */
export function formatClockAndAge(
  iso: string | null | undefined,
  now: number,
): { clock: string; age: string | null; full: string } | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const elapsed = differenceInMinutes(now, date);
  const words = ageWords(Math.abs(elapsed));
  const today = isToday(date);
  let age: string | null;
  if (Math.abs(elapsed) < 1) {
    age = "just now";
  } else if (words === null) {
    age = null;
  } else {
    age = elapsed > 0 ? `${words} ago` : `in ${words}`;
  }
  return {
    clock: today ? format(date, "HH:mm:ss") : format(date, "MMM d, HH:mm"),
    age,
    full: format(date, "EEE, MMM d yyyy 'at' HH:mm:ss"),
  };
}

/**
 * A timestamp as an age: "now", "12m", "3h", "2d", then the date once it is a week old. The full form carries the
 * absolute time for a tooltip. `now` is passed in so the table re-renders every minute from one clock.
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  now: number,
): { short: string; full: string } | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const minutes = Math.max(0, differenceInMinutes(now, date));
  const short =
    minutes < 1
      ? "now"
      : minutes < 60
        ? `${minutes}m`
        : minutes < 60 * 24
          ? `${Math.floor(minutes / 60)}h`
          : minutes < 60 * 24 * 7
            ? `${Math.floor(minutes / (60 * 24))}d`
            : format(date, "MMM d");
  return { short, full: format(date, "EEE, MMM d yyyy 'at' HH:mm:ss") };
}
